<!--
╔════════════════════════════════════════════════════════════════════╗
║  ImageCarousel.vue - Carosello Immagini Interattivo               ║
╚════════════════════════════════════════════════════════════════════╝

DESCRIZIONE:
Componente carosello completo per visualizzazione di multiple immagini
in sequenza. Supporta navigazione via frecce, drag/swipe, puntini, e
lightbox integrato con navigazione immagine.

FEATURES:
  ✓ Navigazione frecce + puntini
  ✓ Drag & swipe gesture (mouse + touch)
  ✓ Lightbox integrato con frecce navigazione
  ✓ Responsive (aspect-ratio adattivo)
  ✓ Animazioni smooth con cubic-bezier
  ✓ Validazione immagini (length > 0)
  ✓ Dimensioni adattive al viewport

LOGICA NAVIGAZIONE:
  - lo scorrimento e' lo scroll nativo del browser (overflow-x + scroll-snap)
  - frecce, puntini e drag col mouse arrivano da useCarouselScroll
  - drag/swipe per navigazione continua
  - puntini per salti diretti a gruppi

DIPENDENZE: nessuna

VIEWPORT/TRACK ARCHITECTURE:
  .carousel-container
    ├── .arrow (frecce navigazione)
    └── .carousel-viewport
        └── .carousel-track (trasla via transform)
            ├── .image-card
            │   └── img.carousel-image
            └── ... (ripetute per ogni immagine)
-->

<template>
  <div class="image-carousel">
    
    <div class="carousel-container">
      
      <button 
        v-show="canScrollLeft"
        class="arrow arrow-left" 
        @click="scrollPrev"
        aria-label="Immagine precedente"
      >
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <button 
        v-show="canScrollRight"
        class="arrow arrow-right" 
        @click="scrollNext"
        aria-label="Immagine successiva"
      >
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <div
        class="carousel-viewport"
        ref="viewport"
        :class="{ 'is-dragging': isDragging }"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
      >
        <div class="carousel-track" ref="track" @dragstart.prevent>
          
          <div
            v-for="(image, index) in images"
            :key="index"
            class="image-card"
          >
            <img 
              :src="image" 
              :alt="`${title} - Immagine ${index + 1}`"
              class="carousel-image"
              @click="openLightbox(index)"
              draggable="false"
            >
          </div>
        </div>
      </div>
    </div>

    <div v-if="totalDots > 1" class="dots-navigation">
      <button
        v-for="(item, index) in totalDots"
        :key="index"
        class="dot"
        :class="{ active: currentDotIndex === index }"
        @click="goToDot(index)"
        :aria-label="`Vai all'immagine ${index + 1}`"
      />
    </div>

    <div v-if="lightboxOpen" class="lightbox" @click="closeLightbox">
      <div class="lightbox-content">
        <img 
          :src="images[lightboxImageIndex]" 
          :alt="`${title} - Immagine ${lightboxImageIndex + 1}`"
          class="lightbox-image"
          @click.stop
        >
        <button class="lightbox-close" @click="closeLightbox">&times;</button>

        <button 
          v-if="lightboxImageIndex > 0"
          class="lightbox-arrow lightbox-arrow-left" 
          @click.stop="lightboxPrevious"
          aria-label="Immagine precedente"
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <button 
          v-if="lightboxImageIndex < images.length - 1"
          class="lightbox-arrow lightbox-arrow-right" 
          @click.stop="lightboxNext"
          aria-label="Immagine successiva"
        >
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * ImageCarousel
 * Lo scorrimento col dito e' lo scroll NATIVO del browser (overflow-x +
 * scroll-snap): su iOS/Android e' il gesto di sistema, non simulato da noi.
 * Frecce, puntini e trascinamento col mouse arrivano da useCarouselScroll.
 */
import { useCarouselScroll } from '@/composables/useCarouselScroll.js'

export default {
  name: 'ImageCarousel',

  props: {
    images: {
      type: Array,
      required: true,
      validator: (arr) => arr.length > 0
    },

    title: {
      type: String,
      default: 'Immagine'
    }
  },

  setup(props) {
    return useCarouselScroll({
      itemCount: () => props.images.length,
      cardSelector: '.image-card'
    })
  },

  data() {
    return {
      lightboxOpen: false,
      lightboxImageIndex: 0
    }
  },

  methods: {
    /**
     * Apre l'immagine a schermo intero.
     * Dopo un trascinamento col mouse il click viene gia' annullato dal
     * composable, quindi qui non serve nessun controllo sul drag.
     */
    openLightbox(index) {
      this.lightboxImageIndex = index
      this.lightboxOpen = true
      document.body.style.overflow = 'hidden'
    },

    closeLightbox() {
      this.lightboxOpen = false
      document.body.style.overflow = ''
    },

    lightboxNext() {
      if (this.lightboxImageIndex < this.images.length - 1) {
        this.lightboxImageIndex++
      }
    },

    lightboxPrevious() {
      if (this.lightboxImageIndex > 0) {
        this.lightboxImageIndex--
      }
    }
  },

  beforeUnmount() {
    document.body.style.overflow = ''
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/main.scss';

/* ──────────────────────────────────────────────────────────────
   CAROUSEL WRAPPER
   ────────────────────────────────────────────────────────────── */
.image-carousel {
  width: 100%;
}

.carousel-container {
  position: relative;
  overflow: visible;
  width: 100%;
}

/* ──────────────────────────────────────────────────────────────
   FRECCE NAVIGAZIONE - Frecce laterali carosello
   ────────────────────────────────────────────────────────────── */
.arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  height: 70px;
  background: rgba($clr-white, 0.65);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba($clr-brand-dark, 0.1);

  svg {
    width: 24px;
    height: 24px;
    color: $clr-brand-dark;
    flex-shrink: 0;
  }

  @media (hover: hover) {
    &:hover {
      background: rgba($clr-white, 1);
      transform: translateY(-50%) scale(1.1);
      box-shadow: 0 6px 20px rgba($clr-brand-dark, 0.15);

      svg {
        color: $clr-tertiary;
      }
    }
  }

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  &.arrow-left {
    left: 10px;
  }

  &.arrow-right {
    right: 10px;
  }
}

/* ──────────────────────────────────────────────────────────────
   VIEWPORT E TRACK - Contenitore scrollabile
   ────────────────────────────────────────────────────────────── */
.carousel-viewport {
  position: relative;
  width: 100%;

  /* Scroll nativo: sul dito ci pensa il browser (inerzia e snap di sistema). */
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  overscroll-behavior-x: contain;
  cursor: grab;

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  &.is-dragging {
    scroll-snap-type: none;
    cursor: grabbing;
    user-select: none;
  }

  /* main.scss mette touch-action: pan-y su tutte le img sotto i 480px:
     qui impedirebbe lo swipe orizzontale nativo. */
  img {
    touch-action: auto;
  }
}

.carousel-track {
  display: flex;
  -webkit-user-drag: none;
  -webkit-tap-highlight-color: transparent;

  &:active {
    cursor: grabbing;
  }
}

/* ──────────────────────────────────────────────────────────────
   IMAGE CARD - Singola immagine nel carosello
   ────────────────────────────────────────────────────────────── */
.image-card {
  scroll-snap-align: start;
  flex: 0 0 100%;
  min-width: 100%;
  max-width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  .carousel-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
    cursor: zoom-in;
    transition: transform 0.3s ease;

    @media (hover: hover) {
      &:hover {
        transform: scale(1.02);
      }
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   PUNTINI NAVIGAZIONE - Dots per salti diretti
   ────────────────────────────────────────────────────────────── */
.dots-navigation {
  display: flex;
  justify-content: center;
  @include flex-gap($space-1);
  margin-top: $space-1;

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    border: none;
    background-color: rgba($clr-text-light, 0.3);
    cursor: pointer;
    transition: all 0.3s ease;
    padding: 0;

    @media (hover: hover) {
      &:hover {
        background-color: rgba($clr-text-light, 0.5);
        transform: scale(1.2);
      }
    }

    &.active {
      background-color: $clr-tertiary;
      width: 24px;
      border-radius: 5px;
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   LIGHTBOX MODAL - Fullscreen viewer
   ────────────────────────────────────────────────────────────── */
.lightbox {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: $space-2;
  cursor: zoom-out;
  animation: fadeIn 0.3s ease;

  .lightbox-content {
    position: relative;
    max-width: 90vw;
    max-height: 90vh;
    display: flex;
    align-items: center;
    justify-content: center;

    .lightbox-image {
      max-width: 100%;
      max-height: 90vh;
      object-fit: contain;
      cursor: default;
      animation: scaleIn 0.3s ease;
    }

    .lightbox-close {
      position: absolute;
      top: -40px;
      right: -40px;
      width: 40px;
      height: 40px;
      background: none;
      border: none;
      color: $clr-white;
      font-size: 3rem;
      cursor: pointer;
      transition: transform 0.2s ease;
      line-height: 1;
      padding: 0;

      @media (hover: hover) {
        &:hover {
          transform: scale(1.1);
        }
      }
    }

    .lightbox-arrow {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      width: 22px;
      height: 70px;
      background: rgba($clr-white, 0.65);
      border: none;
      cursor: pointer;
      z-index: 10;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s ease;
      border-radius: 6px;
      box-shadow: 0 4px 12px rgba($clr-brand-dark, 0.1);
      padding: 0;

      svg {
        width: 24px;
        height: 24px;
        color: $clr-brand-dark;
        flex-shrink: 0;
      }

      @media (hover: hover) {
        &:hover {
          background: rgba($clr-white, 1);
          transform: translateY(-50%) scale(1.1);
          box-shadow: 0 6px 20px rgba($clr-brand-dark, 0.15);

          svg {
            color: $clr-tertiary;
          }
        }
      }

      &:active {
        transform: translateY(-50%) scale(0.95);
      }

      &.lightbox-arrow-left {
        left: 10px;
      }

      &.lightbox-arrow-right {
        right: 10px;
      }
    }
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scaleIn {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

/* ──────────────────────────────────────────────────────────────
   RESPONSIVE - Desktop medio (1300px)
   ────────────────────────────────────────────────────────────── */
@media (max-width: 1300px) {
  .arrow {
    width: 22px;
    height: 60px;

    svg {
      width: 24px;
      height: 24px;
    }
  }

  .dots-navigation {
    margin-top: $space-1;
  }
}

/* ──────────────────────────────────────────────────────────────
   RESPONSIVE - Tablet (1024px)
   ────────────────────────────────────────────────────────────── */
@media (max-width: 1024px) {
  .arrow {
    width: 20px;
    height: 55px;
    background: rgba($clr-white, 0.95);

    svg {
      width: 22px;
      height: 22px;
    }

    &.arrow-left {
      left: 10px;
    }

    &.arrow-right {
      right: 10px;
    }
  }

  .dots-navigation {
    margin-top: $space-1;
  }
}

/* ──────────────────────────────────────────────────────────────
   RESPONSIVE - Tablet piccolo (768px)
   ────────────────────────────────────────────────────────────── */
@media (max-width: 768px) {
  .arrow {
    width: 18px;
    height: 50px;

    svg {
      width: 20px;
      height: 20px;
    }

    &.arrow-left {
      left: 5px;
    }

    &.arrow-right {
      right: 5px;
    }
  }

  .dots-navigation {
    margin-top: $space-0-5;
    @include flex-gap($space-0-5);

    .dot {
      width: 8px;
      height: 8px;

      &.active {
        width: 20px;
      }
    }
  }

  .lightbox {
    padding: $space-1;

    .lightbox-content {
      .lightbox-close {
        top: 10px;
        right: 10px;
        width: 35px;
        height: 35px;
        font-size: 2.5rem;
        background-color: rgba(0, 0, 0, 0.5);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .lightbox-arrow {
        width: 40px;
        height: 40px;

        svg {
          width: 20px;
          height: 20px;
        }

        &.lightbox-arrow-left {
          left: 10px;
        }

        &.lightbox-arrow-right {
          right: 10px;
        }
      }
    }
  }
}

/* ──────────────────────────────────────────────────────────────
   RESPONSIVE - Mobile (480px)
   ────────────────────────────────────────────────────────────── */
@media (max-width: 480px) {
  .arrow {
    width: 16px;
    height: 50px;
    background: rgba($clr-white, 0.65);

    svg {
      width: 10px;
      height: 20px;
    }

    &.arrow-left {
      left: 10px;
    }

    &.arrow-right {
      right: 10px;
    }
  }

  .image-card {
    aspect-ratio: 1 / 1;

    /* simulazione fedele: su Chrome aspect-ratio resta attivo anche con
       .no-aspectratio, quindi in ?legacy=1 lo si annulla per vedere la
       sola ricaduta. Su un dispositivo vecchio vero non serve. */
    .legacy-sim & { aspect-ratio: auto; }

    /* RICADUTA iOS < 15: senza rapporto la card non ha altezza e
       .carousel-image, che usa height:100%, sparisce.
       Qui il padding percentuale PUO' stare sulla card stessa: e' un flex item
       con flex:0 0 100%, quindi la sua larghezza coincide con quella del
       contenitore su cui il padding si calcola, e 100% da' un quadrato.
       L'immagine va ancorata, altrimenti finirebbe sotto il padding. */
    .no-aspectratio & {
      position: relative;
      height: 0;
      padding-top: 100%;

      .carousel-image {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }
    }
  }

  .dots-navigation {
    margin-top: $space-1;

    .dot {
      width: 6px;
      height: 6px;

      &.active {
        width: 16px;
      }
    }
  }

  .lightbox {
    padding: $space-0-5;

    .lightbox-content {
      .lightbox-close {
        width: 30px;
        height: 30px;
        font-size: 2rem;
        top: 5px;
        right: 5px;
      }

      .lightbox-arrow {
        width: 16px;
        height: 50px;

        svg {
          width: 10px;
          height: 20px;
        }

        &.lightbox-arrow-left {
          left: 10px;
        }

        &.lightbox-arrow-right {
          right: 10px;
        }
      }
    }
  }
}
</style>